import { useTranslation } from "react-i18next";
import { convertPxToRem } from "@/utils";
import { useState } from "react";
import { Stack, Typography, InputLabel } from "@mui/material";
import ButtonWithAnalytics from "../ButtonWithAnalytics";
import SlideUpDialog from "../SlideUpDialog";
import VoucherInputField from "@/features/Vouchering/components/VoucherInputField";

export enum CurrentPath {
  MyDiscountVouchers = "MyDiscountVouchers",
  PaymentOptions = "PaymentOptions",
}

interface DiscountVoucherCaptureModalProps {
  isModalOpen: boolean;
  onModalClose: () => void;
  errorMessage: string;
  currentPath: CurrentPath;
  handleApplyVoucher: (
    voucherCode: string,
    isUseVoucherLater?: boolean
  ) => void;
  currentVoucherCode: string | null;
}

export default function DiscountVoucherCaptureModal({
  isModalOpen,
  onModalClose,
  errorMessage,
  currentPath,
  handleApplyVoucher,
  currentVoucherCode,
}: DiscountVoucherCaptureModalProps) {
  const { t } = useTranslation();

  const [voucherCode, setVoucherCode] = useState(currentVoucherCode || "");

  const [hasError, setHasError] = useState(Boolean(errorMessage));
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [isValidCode, setIsValidCode] = useState(false);

  const handleVoucherCodeChange = (code: string) => {
    setVoucherCode(code);
    if (errorMessage) {
      setHasError(false);
    }
  };
  const voucherCodeRegex = /^[A-Z]{4}-[0-9]{4}-[A-Z]{4}$/;

  const formatVoucherCode = (value: string) => {
    const cleanedValue = value.replace(/[^A-Z0-9]/gi, "").toUpperCase();

    const limitedValue = cleanedValue.slice(0, 12);

    let formatted = "";
    for (let i = 0; i < limitedValue.length; i++) {
      if (i === 4 || i === 8) {
        formatted += "-";
      }
      formatted += limitedValue[i];
    }

    return formatted;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const formattedValue = formatVoucherCode(value);

    setVoucherCode(formattedValue);
    handleVoucherCodeChange(formattedValue);

    const cleanedValue = formattedValue.replace(/-/g, "");

    if (cleanedValue.length === 12) {
      if (voucherCodeRegex.test(formattedValue)) {
        setLocalErrorMessage("");
        setIsValidCode(true);
      } else {
        setLocalErrorMessage(
          t("AddOrUseVoucher.Voucher.UseVoucherTab.errorMessage.generic")
        );
        setIsValidCode(false);
        setHasError(true);
      }
    } else {
      setLocalErrorMessage("");
      setIsValidCode(false);
      setHasError(false);
    }
  };

  return (
    <SlideUpDialog
      isOpen={isModalOpen}
      onClose={() => {
        onModalClose();
        setHasError(false);
      }}
    >
      <Stack
        textAlign="center"
        spacing={3}
        sx={{ marginTop: `${convertPxToRem(16)} !important` }}
        aria-label="Discount Voucher Capture Modal"
      >
        <Stack
          spacing={1}
          sx={{
            cursor: "pointer",
            border: "none",
            background: "transparent",
            textAlign: "left",
          }}
        >
          <Typography variant="h4">
            {t("DiscountVoucherCaptureModal.title")}
          </Typography>
          <Typography variant="body2" color="neutral.light">
            {currentPath === CurrentPath.PaymentOptions
              ? t("DiscountVoucherCaptureModal.description")
              : t("DiscountVoucherCaptureModal.descriptionV2")}
          </Typography>
          <Stack sx={{ width: "100%" }}>
            <InputLabel>
              <Typography
                textAlign="left"
                variant="body2"
                color={hasError || localErrorMessage ? "error" : "neutral.main"}
              >
                {t("DiscountVoucherCaptureModal.inputLabel")}
              </Typography>
            </InputLabel>
            <VoucherInputField
              handleInputChange={handleInputChange}
              localErrorMessage={localErrorMessage}
              setError={setHasError}
              setLocalErrorMessage={setLocalErrorMessage}
              error={hasError}
              errorMessage={hasError ? errorMessage : ""}
              voucherCode={voucherCode}
            />
          </Stack>
        </Stack>

        {currentPath === CurrentPath.PaymentOptions ? (
          <Stack direction="row" spacing={2}>
            <ButtonWithAnalytics
              intent="confirmational"
              page="DiscountVoucherCaptureModal"
              text={t("DiscountVoucherCaptureModal.secondaryButtonText")}
              fullWidth
              variant="outlined"
              disabled={!isValidCode}
              onClick={() => handleApplyVoucher(voucherCode || "", true)}
            >
              {t("DiscountVoucherCaptureModal.secondaryButtonText")}
            </ButtonWithAnalytics>
            <ButtonWithAnalytics
              intent="confirmational"
              page="DiscountVoucherCaptureModal"
              text={t("DiscountVoucherSlideUpModal.primaryButtonText")}
              fullWidth
              variant="contained"
              disabled={!isValidCode}
              onClick={() => handleApplyVoucher(voucherCode || "")}
            >
              {t("DiscountVoucherSlideUpModal.primaryButtonText")}
            </ButtonWithAnalytics>
          </Stack>
        ) : (
          <Stack>
            <ButtonWithAnalytics
              intent="confirmational"
              page="DiscountVoucherCaptureModal"
              text={t("DiscountVoucherCaptureModal.primaryButtonText")}
              fullWidth
              variant="contained"
              disabled={!isValidCode}
              onClick={() => handleApplyVoucher(voucherCode || "")}
            >
              {t("DiscountVoucherCaptureModal.primaryButtonText")}
            </ButtonWithAnalytics>
          </Stack>
        )}
      </Stack>
    </SlideUpDialog>
  );
}
